import { useRouter } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useOptionalProject } from "@/features/project/project-context"
import { personPagePathFor } from "./knowledge-model"

const plainClick = (event: React.MouseEvent) =>
  !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)

/**
 * Decision 0086 (build map §6.1): a person's avatar or name opens their page in Knowledge (`/knowledge/people/<id>`).
 * A real anchor routed in place on a plain click, like `AgentLink`. The workspace comes from `workspaceId` or the
 * project around it; with neither it is just its children.
 */
export function PersonLink({
  userId,
  workspaceId,
  children,
  className,
  title,
}: {
  userId: string
  workspaceId?: string | undefined
  children: ReactNode
  className?: string | undefined
  title?: string | undefined
}) {
  const ctx = useOptionalProject()
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined
  const ws = workspaceId ?? ctx?.workspaceId
  if (!ws) return <span className={className}>{children}</span>
  const href = personPagePathFor(ws, userId)
  return (
    <a
      href={href}
      title={title}
      data-person-link={userId}
      className={className}
      onClick={(event) => {
        event.stopPropagation()
        if (!router || !plainClick(event)) return
        event.preventDefault()
        router.history.push(href)
      }}
    >
      {children}
    </a>
  )
}
